<script setup lang="ts">
import { ErrorState, PageHeader, PageShell, Spinner } from '@taler/ui'
import DetailNotFound from '@/app/components/DetailNotFound.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import {
  canManageReceiptScans,
  type EditReceiptExtractionInput,
  ReceiptReviewForm,
  useReceiptScan,
} from '@/features/receipt-scans'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()
const route = useRoute()
const router = useRouter()

const canView = computed(() => canManageReceiptScans(session.can))
const ledgerId = () => session.activeLedger.value?.id
const jobId = () => (typeof route.params['jobId'] === 'string' ? route.params['jobId'] : undefined)

const scan = useReceiptScan({ ledgerId, jobId, enabled: () => canView.value })

async function onCommit(input: EditReceiptExtractionInput): Promise<void> {
  const saved = await scan.save(input).catch(() => undefined)
  if (saved === undefined || saved.status !== 'READY') return
  await scan.commit().catch(() => undefined)
}

function onRescan(): void {
  void scan.rescan()
}

function onViewEntry(entryId: string): void {
  void router.push({ name: 'transactions', query: { entry: entryId } })
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('receiptScan.title')">
      <template #actions>
        <RouterLink :to="{ name: 'receipt-scan' }" class="text-[color:var(--color-text-muted)] underline">
          {{ t('receiptScan.startOver') }}
        </RouterLink>
      </template>
    </PageHeader>

    <p v-if="!canView" class="text-[color:var(--color-text-muted)]">{{ t('receiptScan.noAccess') }}</p>

    <p v-else-if="scan.isLoading.value" role="status">{{ t('receiptScan.loading') }}</p>

    <DetailNotFound
      v-else-if="scan.notFound.value"
      :to="{ name: 'receipt-scan' }"
      :list="t('receiptScan.title')"
    />

    <ErrorState
      v-else-if="scan.error.value"
      :title="t('receiptScan.scanErrorTitle')"
      :body="t('receiptScan.errorBody')"
      :retry-label="t('receiptScan.retry')"
      :retrying="scan.retrying.value"
      @retry="scan.retry()"
    />

    <p
      v-else-if="scan.job.value?.status === 'PENDING'"
      role="status"
      class="flex items-center gap-[var(--space-inline)] text-[color:var(--color-text-muted)]"
    >
      <Spinner />
      {{ t('receiptScan.analysing') }}
    </p>

    <ReceiptReviewForm
      v-else-if="scan.job.value !== undefined"
      :job="scan.job.value"
      :suggestions="scan.suggestions.value"
      :contact-account-ids="scan.contactAccountIds.value"
      :image-url="scan.imageUrl.value"
      :is-saving="scan.isSaving.value"
      :is-committing="scan.isCommitting.value"
      :is-rescanning="scan.isRescanning.value"
      :save-error-detail="scan.saveErrorDetail.value"
      :commit-error-detail="scan.commitErrorDetail.value"
      @commit="onCommit"
      @rescan="onRescan"
      @view-entry="onViewEntry"
    />
  </PageShell>
</template>
